/**
 * useSidebarSync - Hook for syncing form state with sidebar context
 *
 * Handles bidirectional sync between:
 * 1. Form validation state → Sidebar checklist display
 * 2. Sidebar tab selection → Form tab
 *
 * Works with both retro and light themes via RetroContext.
 *
 * @module hooks/useSidebarSync
 */

import { useEffect, useRef } from 'react';

/**
 * @typedef {Object} SidebarSyncOptions
 * @property {Object} requiredFields - Required fields validation state
 * @property {Array} missingFields - List of missing required fields
 * @property {Function|null} setScenarioValidation - Context setter for validation state
 * @property {number} scenarioTab - Current tab index from sidebar context
 * @property {number} formTab - Current tab index from form state
 * @property {Function} setFormTab - Setter for form tab
 * @property {Function|null} setScenarioTab - Context setter for sidebar tab
 * @property {boolean} canProceedToNextTab - Whether validation allows proceeding
 * @property {React.MutableRefObject<boolean>} hasInitializedFromHash - Whether tab was initialized from URL hash
 */

/**
 * Hook for syncing form state with sidebar context
 *
 * Handles:
 * 1. Pushing validation state to context for sidebar checklist
 * 2. Syncing sidebar tab selection back to form state
 * 3. Validating tab navigation based on required fields
 *
 * @param {SidebarSyncOptions} options
 */
export function useSidebarSync({
    requiredFields,
    missingFields,
    setScenarioValidation,
    scenarioTab,
    formTab,
    setFormTab,
    setScenarioTab,
    canProceedToNextTab,
    hasInitializedFromHash,
}) {
    // Sync validation state to retro context for sidebar checklist display
    useEffect(() => {
        if (setScenarioValidation) {
            setScenarioValidation({ requiredFields, missingFields });
        }
        // Cleanup when leaving scenario editor
        return () => {
            if (setScenarioValidation) {
                setScenarioValidation({ requiredFields: {}, missingFields: [] });
            }
        };
    }, [requiredFields, missingFields, setScenarioValidation]);

    // Track previous scenarioTab to distinguish user clicks from context resets
    const prevScenarioTabRef = useRef(scenarioTab);

    // Sync sidebar navigation → form tab (when sidebar navigation changes tab)
    // Works for both retro and light modes since both use scenarioTab from RetroContext
    useEffect(() => {
        const prevSidebarTab = prevScenarioTabRef.current;
        prevScenarioTabRef.current = scenarioTab;

        // Skip sync if tab was just initialized from URL hash to prevent overriding.
        // Only skip when sidebar hasn't actually changed (mount-time effect),
        // not when user actively clicks a different sidebar tab.
        if (hasInitializedFromHash?.current) {
            hasInitializedFromHash.current = false;
            if (scenarioTab === formTab) {
                return;
            }
        }

        if (scenarioTab !== formTab) {
            // Context reset detection: sidebar was ALREADY at 0 (didn't just change to 0),
            // but form loaded at a higher tab from URL. Push sidebar up to match form.
            // If prevSidebarTab !== 0, the user actively clicked tab 0 — let it through.
            if (scenarioTab === 0 && formTab > 0 && prevSidebarTab === 0) {
                setScenarioTab?.(formTab);
                return;
            }

            // Validation check - prevent skipping ahead without required fields
            // Allow navigation if results exist (even with missing fields)
            if (scenarioTab > 2 && !canProceedToNextTab) {
                // Reset context tab back to form tab if validation fails
                setScenarioTab?.(formTab);
                return;
            }

            // Sync form tab to sidebar tab (user clicked sidebar navigation)
            setFormTab(scenarioTab);
        }
    }, [scenarioTab, formTab, canProceedToNextTab, setScenarioTab, setFormTab, hasInitializedFromHash]);
}

export default useSidebarSync;
